import { FocusMetrics, PeriodBreakdownItem, SportBreakdownEntry, StatsResponse } from '../../models/stats.models';
import {
  MetricMode,
  formatDistributionPercent,
  formatSeasonDurationHours,
  formatStackedChartTooltipValue,
  formatYAxisTick,
  getDurationDistanceMetric,
  getFocusColor,
  getFocusLabel,
  getSportTypeColor,
  toChartValue,
} from '../../pages/training-log/training-log.helpers';
import {
  getStackedSportRowTotal,
  getStackedSportYDomain,
  StackedSportChartRow,
} from '../sport-breakdown/sport-breakdown.helpers';

export type { MetricMode };
export { formatDistributionPercent, formatYAxisTick };
export { formatStackedChartTooltipValue as formatSportChartValue };

export interface SportTypeLegendItem {
  key: string;
  label: string;
  rawValue: number;
  color: string;
  formattedTotal: string;
}

export interface FocusPanelItem {
  key: string;
  label: string;
  percent: number;
  durationSeconds: number;
  distance: number;
  sessions: number;
  color: string;
}

const hasSportTypeMetrics = (entry: SportBreakdownEntry | undefined): boolean => {
  if (!entry) {
    return false;
  }
  const sportTypes = Object.values(entry.sportType ?? {});
  return sportTypes.some((type) => (type.duration ?? 0) > 0 || (type.distance ?? 0) > 0);
};

const hasSportEntryData = (entry: SportBreakdownEntry | undefined): boolean => {
  if (!entry) {
    return false;
  }
  return (entry.duration ?? 0) > 0 || (entry.distance ?? 0) > 0 || hasSportTypeMetrics(entry);
};

export const formatSportTypeLabel = (typeKey: string): string => typeKey;

export const getSportsWithTypeData = (stats: StatsResponse | null): string[] => {
  if (!stats) {
    return [];
  }

  const keys = new Set<string>();
  Object.entries(stats.sportBreakdown ?? {}).forEach(([key, entry]) => {
    if (hasSportEntryData(entry)) {
      keys.add(key);
    }
  });
  (stats.periodBreakdown ?? []).forEach((period) => {
    Object.entries(period.sport ?? {}).forEach(([key, entry]) => {
      if (hasSportEntryData(entry)) {
        keys.add(key);
      }
    });
  });

  return Array.from(keys).sort((a, b) => {
    const aTotal = stats.sportBreakdown?.[a]?.duration ?? 0;
    const bTotal = stats.sportBreakdown?.[b]?.duration ?? 0;
    return bTotal - aTotal;
  });
};

export const getSportTypeKeys = (
  sportKey: string,
  periods: PeriodBreakdownItem[],
  sportBreakdown: Record<string, SportBreakdownEntry>
): string[] => {
  const keys = new Set<string>();
  periods.forEach((period) => {
    Object.keys(period.sport?.[sportKey]?.sportType ?? {}).forEach((key) => keys.add(key));
  });
  Object.keys(sportBreakdown?.[sportKey]?.sportType ?? {}).forEach((key) => keys.add(key));

  return Array.from(keys).sort((a, b) => {
    const aTotal = getDurationDistanceMetric(sportBreakdown?.[sportKey]?.sportType?.[a], 'duration');
    const bTotal = getDurationDistanceMetric(sportBreakdown?.[sportKey]?.sportType?.[b], 'duration');
    return bTotal - aTotal;
  });
};

export const buildPeriodSportTypeChartData = (
  periods: PeriodBreakdownItem[],
  sportKey: string,
  sportTypeKeys: string[],
  mode: MetricMode
): StackedSportChartRow[] => {
  return periods.map((period) => {
    const row: StackedSportChartRow = { label: `P${period.index}` };

    sportTypeKeys.forEach((typeKey) => {
      const rawValue = getDurationDistanceMetric(period.sport?.[sportKey]?.sportType?.[typeKey], mode);
      row[typeKey] = toChartValue(rawValue, mode);
    });

    return row;
  });
};

export const buildSportTypeLegendItems = (
  sportKey: string,
  sportIndex: number,
  sportBreakdown: Record<string, SportBreakdownEntry>,
  sportTypeKeys: string[],
  mode: MetricMode
): SportTypeLegendItem[] => {
  return sportTypeKeys.map((typeKey, typeIndex) => {
    const rawValue = getDurationDistanceMetric(sportBreakdown?.[sportKey]?.sportType?.[typeKey], mode);
    const formattedTotal =
      mode === 'duration'
        ? formatSeasonDurationHours(rawValue)
        : `${rawValue % 1 === 0 ? rawValue : rawValue.toFixed(1)}`;

    return {
      key: typeKey,
      label: formatSportTypeLabel(typeKey),
      rawValue,
      color: getSportTypeColor(sportKey, sportIndex, typeIndex, sportTypeKeys.length),
      formattedTotal,
    };
  });
};

export const buildFocusPanelItems = (focus: Record<string, FocusMetrics> | undefined): FocusPanelItem[] => {
  const entries = Object.entries(focus ?? {}).map(([key, metrics], index) => ({
    key,
    label: getFocusLabel(key),
    durationSeconds: metrics?.duration ?? 0,
    distance: metrics?.distance ?? 0,
    sessions: metrics?.sessions ?? 0,
    color: getFocusColor(key, index),
  }));

  const totalDuration = entries.reduce((sum, entry) => sum + entry.durationSeconds, 0);

  return entries
    .map((entry) => ({
      ...entry,
      percent: totalDuration > 0 ? (entry.durationSeconds / totalDuration) * 100 : 0,
    }))
    .filter((entry) => entry.durationSeconds > 0 || entry.distance > 0 || entry.sessions > 0)
    .sort((a, b) => b.durationSeconds - a.durationSeconds);
};

export const getStackedSportTypeYDomain = (
  rows: StackedSportChartRow[],
  sportTypeKeys: string[],
  mode: MetricMode
): [number, number] => {
  return getStackedSportYDomain(rows, sportTypeKeys, mode);
};

export const getStackedSportTypeRowTotal = (row: StackedSportChartRow, sportTypeKeys: string[]): number => {
  return getStackedSportRowTotal(row, sportTypeKeys);
};

export const hasSportTypeData = (stats: StatsResponse | null): boolean => {
  return getSportsWithTypeData(stats).length > 0;
};
